import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatDateShort } from '../lib/datetime'

interface Row {
  id: string
  email: string
  display_name: string
  role: 'admin' | 'rattare' | 'deltagare'
  created_at: string
}

interface Group {
  id: string
  name: string
  is_default: boolean
}

const ROLES = [
  { value: 'deltagare', label: 'Deltagare' },
  { value: 'rattare', label: 'Rättare' },
  { value: 'admin', label: 'Admin' },
] as const

/**
 * Adminvy för deltagare.
 *
 * Alla registrerar sig själva, men visningsnamnet gissas ur e-postadressen
 * vid signup — `kalle.svensson@` blir "Kalle Svensson", `kalle@` bara
 * "Kalle". Efternamn får alltså fyllas i för hand, precis som förr.
 *
 * Här utses också rättaren, och här knyts deltagaren till ett gäng utan att
 * gå omvägen via inbjudningslänk och lösenord. Bytet är en flytt: databasen
 * tar bort övriga medlemskap, så ingen hamnar i två gäng och får frågan om
 * vilket laget gäller.
 */
export function AdminUsers() {
  const { profile } = useAuth()
  const [rows, setRows] = useState<Row[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  /** user_id → group_id. Ett gäng per deltagare är det normala efter 0028. */
  const [memberOf, setMemberOf] = useState<Record<string, string>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const load = useCallback(async () => {
    const [p, g, m] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, email, display_name, role, created_at')
        .order('display_name'),
      supabase.from('groups').select('id, name, is_default').order('name'),
      // group_roster ger medlemskapen i samma form som gäng-sidan använder.
      // Admin ser alla via RLS; en deltagare hade bara sett sina egna, men
      // den här sidan är ändå adminskyddad.
      supabase.from('group_roster').select('group_id, group_name, user_id'),
    ])

    if (p.error) setError(p.error.message)
    else setRows((p.data as Row[]) ?? [])

    if (g.error) setError(g.error.message)
    else setGroups((g.data as Group[]) ?? [])

    if (!m.error) {
      const map: Record<string, string> = {}
      for (const r of (m.data as Array<{ group_id: string; user_id: string | null }>) ?? []) {
        if (r.user_id) map[r.user_id] = r.group_id
      }
      setMemberOf(map)
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  async function saveName(r: Row) {
    const next = (drafts[r.id] ?? r.display_name).trim()
    if (!next || next === r.display_name) return

    setSavingId(r.id)
    setError(null)

    const { error } = await supabase
      .from('profiles')
      .update({ display_name: next })
      .eq('id', r.id)

    setSavingId(null)
    if (error) { setError(error.message); return }

    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, display_name: next } : x)))
    setSavedId(r.id)
    setTimeout(() => setSavedId(null), 2000)
  }

  /**
   * Flytten görs i databasen och inte med två skrivningar härifrån: en
   * insert som lyckas och en delete som inte gör det hade lämnat deltagaren
   * i två gäng, och det syns först när draftvyn börjar fråga vilket laget
   * gäller.
   */
  async function setGroup(r: Row, groupId: string) {
    setSavingId(r.id)
    setError(null)

    const { data, error } = await supabase.rpc('admin_set_user_group', {
      p_user_id: r.id,
      p_group_id: groupId || null,
    })

    setSavingId(null)
    if (error) { setError(error.message); return }

    setMemberOf((prev) => ({ ...prev, [r.id]: data as string }))
    setSavedId(r.id)
    setTimeout(() => setSavedId(null), 2000)
  }

  async function setRole(r: Row, role: Row['role']) {
    setError(null)
    const { error } = await supabase.from('profiles').update({ role }).eq('id', r.id)

    if (error) { setError(error.message); return }
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, role } : x)))
  }

  /**
   * Borttagningen går genom admin_delete_user som raderar i auth.users —
   * roten. Kaskaden tar profil, lag, tips och medlemskap. Typfallet är ett
   * dödfött konto: registrerat med en adress vars spamfilter äter magic-
   * linken, så personen ska registrera om sig med en annan.
   */
  async function removeUser(r: Row) {
    setSavingId(r.id)
    setError(null)

    const { error } = await supabase.rpc('admin_delete_user', { p_user_id: r.id })

    setSavingId(null)
    setConfirmDeleteId(null)
    setDeleteConfirmText('')
    if (error) { setError(error.message); return }
    await load()
  }

  const admins = rows.filter((r) => r.role === 'admin').length

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1>Deltagare</h1>
        <p className="muted">
          Visningsnamnet gissas ur e-postadressen vid registrering, så efternamn
          får ofta fyllas i här. Deltagarna kan inte ändra sin egen roll.
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {rows.map((r) => (
        <article key={r.id} className="card stack" style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label htmlFor={`name-${r.id}`}>Visningsnamn</label>
              <input
                id={`name-${r.id}`}
                type="text"
                value={drafts[r.id] ?? r.display_name}
                onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                onBlur={() => void saveName(r)}
              />
            </div>

            <div style={{ minWidth: 150 }}>
              <label htmlFor={`role-${r.id}`}>Roll</label>
              <select
                id={`role-${r.id}`}
                value={r.role}
                disabled={r.id === profile?.id || (r.role === 'admin' && admins === 1)}
                onChange={(e) => void setRole(r, e.target.value as Row['role'])}
              >
                {ROLES.map((x) => (
                  <option key={x.value} value={x.value}>{x.label}</option>
                ))}
              </select>
            </div>

            <div style={{ minWidth: 180 }}>
              <label htmlFor={`group-${r.id}`}>Gäng</label>
              <select
                id={`group-${r.id}`}
                value={memberOf[r.id] ?? ''}
                onChange={(e) => void setGroup(r, e.target.value)}
              >
                {/* Standardgänget står med i listan som vilket gäng som helst,
                    märkt "(standard)". Ett separat tomt val ovanför hette
                    "Standardgänget" och gjorde samma sak — två rader för
                    samma handling, och man fick fråga sig vad skillnaden var. */}
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.is_default ? ' (standard)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="muted">
            {r.email} · med sedan {formatDateShort(r.created_at)}
            {savingId === r.id && ' · sparar…'}
            {savedId === r.id && ' · sparat'}
            {r.id === profile?.id && ' · det här är du'}
            {r.id !== profile?.id && confirmDeleteId !== r.id && (
              <>
                {' · '}
                <button
                  type="button"
                  className="link"
                  onClick={() => { setConfirmDeleteId(r.id); setDeleteConfirmText('') }}
                >
                  Ta bort kontot
                </button>
              </>
            )}
          </p>

          {confirmDeleteId === r.id && (
            <div className="stack">
              <div className="notice notice--error">
                Kontot, laget, tipsen och medlemskapen försvinner — går inte att
                ångra. Skriv visningsnamnet för att bekräfta:{' '}
                <strong>{r.display_name}</strong>
              </div>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={r.display_name}
                autoComplete="off"
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => void removeUser(r)}
                  disabled={savingId === r.id || deleteConfirmText.trim() !== r.display_name.trim()}
                >
                  {savingId === r.id ? 'Tar bort…' : 'Ja, ta bort'}
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmDeleteId(null); setDeleteConfirmText('') }}
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </article>
      ))}

      {rows.length > 0 && admins === 1 && (
        <p className="muted">
          Det finns bara en admin. Rollen går inte att ta bort från det kontot —
          utan admin kan ingen lägga upp turneringar eller utse roller.
        </p>
      )}
    </div>
  )
}
